import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import Formsy from 'formsy-react';
import { Grid, Card, CardContent, Typography, Button, FormHelperText } from '@mui/material';
import PageviewIcon from '@mui/icons-material/Pageview';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import TextInput from 'components/inputs/TextInput';
import SelectInput from 'components/inputs/SelectInput';
import Alert from '../Alert';
import LoadingSpinner from '../LoadingSpinner';
import ReCAPTCHA from 'react-google-recaptcha';
import _ from 'lodash';
import { submissionModelName } from 'utils/contentHelper';
import { createSupportLog } from 'actions/SupportActions';
import renderHTML from 'html-react-parser';
import { Helmet } from 'react-helmet';

export class ContactUsPage extends React.Component {
  constructor(props) {
    super(props);

    this.handleSubmit = this.handleSubmit.bind(this);
    this.formRef = React.createRef();
    this.onCaptchaChange = this.onCaptchaChange.bind(this);
    this.handleCategoryChange = this.handleCategoryChange.bind(this);

    this.state = {
      selectedOption: null,
      showMessage: false,
      categoryNote: null,
      captchaResponse: null,
      captchaError: null
    }
  }

  handleOptionChange(option) {
    this.setState({selectedOption: option});
  }

  handleCategoryChange(event) {
    var cat = _.find(this.props.promotion.support_categories, (c) => { return c.name == event.target.value });
    this.setState({showMessage: cat.allow_message, categoryNote: cat.note});
  }

  handleSubmit(data, _resetForm, _invalidateForm) {
    const { promotion, createSupportLog } = this.props;

    if (this.state.captchaResponse) {
      this.setState({captchaError: null, showMessage: false, categoryNote: null});
      createSupportLog(_.merge(data, {promotion_id: promotion.id}), this.state.captchaResponse);
    } else {
      this.setState({captchaError: 'Captcha is required'});
    }
  }

  onCaptchaChange(captchaResponse) {
    this.setState({captchaResponse: captchaResponse, captchaError: null});
  }

  render() {
    const { promotion, createSuccess, isProcessing } = this.props;
    const { selectedOption, showMessage, categoryNote } = this.state;

    return (
      <React.Fragment>
        <Helmet>
          <title>{promotion.name} | Contact Us</title>
        </Helmet>

        <Typography variant="h4" gutterBottom align="center">
          Contact Us
        </Typography>
        <Typography variant="subtitle1" paragraph align="center">
          What action would you like to take?
        </Typography>

        <Grid container spacing={2} justifyContent="center" className="gutter-bottom">
          {promotion.trackable &&
            <Grid size={{md: 4, xs: 8}}>
              <Card raised={selectedOption == 'track'}>
                <CardContent className="center pointer"
                  onClick={() => this.handleOptionChange('track')}
                  onKeyDown={(e) => {if (e.keyCode == 13 || e.keyCode == 32) {this.handleOptionChange('track')}}}
                  style={{paddingBottom: '8px'}}
                  role="button"
                  tabIndex="0"
                  aria-pressed={selectedOption == 'track'}
                >
                  <PageviewIcon color="primary" style={{fontSize: 64}} />
                  <Typography variant="subtitle1" component="div" gutterBottom style={{display: 'block', minHeight: '60px'}}>
                    Track {submissionModelName(promotion, true)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          }
          <Grid size={{md: 4, xs: 8}}>
            <Card raised={selectedOption == 'contact'}>
              <CardContent className="center pointer"
                onClick={() => this.handleOptionChange('contact')}
                onKeyDown={(e) => {if (e.keyCode == 13 || e.keyCode == 32) {this.handleOptionChange('contact')}}}
                style={{paddingBottom: '8px'}}
                role="button"
                tabIndex="0"
                aria-pressed={selectedOption == 'contact'}
              >
                <ContactSupportIcon color="primary" style={{fontSize: 64}} />
                <Typography variant="subtitle1" component="div" gutterBottom style={{display: 'block', minHeight: '60px'}}>
                  Contact Support
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {selectedOption == 'track' &&
          <React.Fragment>
            <Typography variant="subtitle1" align="center" className="gutter-top" gutterBottom style={{display: 'block'}}>
              You can get an immediate update as to what state your {submissionModelName(promotion)} is in by visiting the Track {submissionModelName(promotion, true)} page
            </Typography>
            <Typography variant="h5" align="center">
              <NavLink exact to={{pathname: '/track'}}>Track {submissionModelName(promotion, true)}</NavLink>
            </Typography>
          </React.Fragment>
        }
        {selectedOption == 'contact' &&
          <Formsy noValidate onValidSubmit={this.handleSubmit} ref={this.formRef}>
            <LoadingSpinner show={isProcessing} />
            <Typography variant="subtitle1" align="center">
              {renderHTML(promotion.contact_us_content)}
            </Typography>
            <Grid container spacing={1} justifyContent="center">
              {createSuccess ?
                <Alert type="success" message="Thank you. We have successfully received your enquiry. A member of our team will respond to you as soon as possible" hideClose />
              :
                <Grid size={{sm: 8, xs: 11}}>
                  {promotion.has_support_categories &&
                    <SelectInput name="category" label="Category" required
                      options={promotion.support_categories.map(cat => ({value: cat.name, label: cat.name}))}
                      onChange={this.handleCategoryChange}
                      validationErrors={{isDefaultRequiredValue: "can't be blank"}}
                    />
                  }

                  {categoryNote &&
                    <Alert type="info" message={categoryNote} hideClose />
                  }

                  <TextInput name="name" label="Name" required
                    validationErrors={{isDefaultRequiredValue: "can't be blank"}}
                  />

                  <TextInput type="email" name="email" label="Email" required
                    validations={{isEmail: true}}
                    validationErrors={{
                      isEmail: 'not a valid email, please use the following format: user@example.com',
                      isDefaultRequiredValue: "can't be blank"
                    }}
                  />

                  <TextInput type="tel" name="phone_number" label="Phone Number" required
                    validations={{isNumeric: true, isLength: 10}}
                    validationErrors={{
                      isNumeric: 'must be a number',
                      isLength: 'must be 10 digits',
                      isDefaultRequiredValue: "can't be blank"
                    }}
                  />

                  <TextInput name="claim_id" label={submissionModelName(promotion, true) + ' ID'}
                    validations={{isNumeric: true, isLength: 8}}
                    validationErrors={{
                      isNumeric: 'must be a number',
                      isLength: 'must be 8 digits'
                    }}
                  />

                  {(showMessage || !promotion.has_support_categories) &&
                    <TextInput name="message" label="Message" required={showMessage === false} multiline
                      validationErrors={{isDefaultRequiredValue: "can't be blank"}}
                    />
                  }

                  <ReCAPTCHA
                    sitekey={window.CAPTCHA_CLIENT_KEY || ''}
                    onChange={this.onCaptchaChange}
                    className="g-recaptcha"
                  />
                  {this.state.captchaError &&
                    <FormHelperText error>{this.state.captchaError}</FormHelperText>
                  }

                </Grid>
              }
            </Grid>
            {!createSuccess &&
              <div className="button-container">
                <Button type="submit" variant="contained" color="primary">Submit</Button>
              </div>
            }
          </Formsy>
        }
      </React.Fragment>
    )
  }
}

function mapStateToProps(state) {
  return {
    ...state.support
  };
}

function mapDispatchToProps(dispatch) {
  return {
    createSupportLog: (data, captchaResponse) => {
      dispatch(createSupportLog(data, captchaResponse));
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ContactUsPage);
